import { httpClient } from '@/core/api/httpClient';
import type {
  BoardGroup,
  CreatedResponse,
  CreateBoardGroupPayload,
  UpdateBoardGroupPayload,
  ReorderTaskGroupsRequest,
} from '../types';
import { mockBoardGroups } from './board-groups.mock';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

const boardGroupsMock: BoardGroup[] = [...mockBoardGroups];

export const boardGroupsService = {
  getById: async (boardId: string, groupId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      const group = boardGroupsMock.find((g) => g.id === groupId && g.boardId === boardId) ?? null;
      return Promise.resolve({ data: group });
    }
    return signal
      ? httpClient.get<BoardGroup>(`${API_V1}/boards/${boardId}/groups/${groupId}`, { signal })
      : httpClient.get<BoardGroup>(`${API_V1}/boards/${boardId}/groups/${groupId}`);
  },

  reorder: async (boardId: string, data: ReorderTaskGroupsRequest) => {
    if (USE_MOCK) {
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.patch<ReorderTaskGroupsRequest, void>(
      `${API_V1}/boards/${boardId}/groups/reorder`,
      data,
    );
  },

  getByBoard: async (boardId: string, signal?: AbortSignal) => {
    if (USE_MOCK) {
      return Promise.resolve({
        data: boardGroupsMock
          .filter((g) => g.boardId === boardId)
          .sort((a, b) => a.name.localeCompare(b.name)),
      });
    }
    return signal
      ? httpClient.get<BoardGroup[]>(`${API_V1}/boards/${boardId}/groups`, { signal })
      : httpClient.get<BoardGroup[]>(`${API_V1}/boards/${boardId}/groups`);
  },

  create: async (boardId: string, data: CreateBoardGroupPayload) => {
    if (USE_MOCK) {
      const newGroup: BoardGroup = {
        id: `bg-${Date.now()}`,
        boardId,
        name: data.name,
        description: data.description ?? null,
        order: boardGroupsMock.filter((g) => g.boardId === boardId).length,
        createdAt: new Date().toISOString(),
        updatedAt: null,
      };
      boardGroupsMock.push(newGroup);
      return Promise.resolve({ data: { id: newGroup.id } as CreatedResponse });
    }
    return httpClient.post<CreateBoardGroupPayload, CreatedResponse>(
      `${API_V1}/boards/${boardId}/groups`,
      data,
    );
  },

  update: async (boardId: string, groupId: string, data: UpdateBoardGroupPayload) => {
    if (USE_MOCK) {
      const idx = boardGroupsMock.findIndex((g) => g.id === groupId && g.boardId === boardId);
      if (idx !== -1) boardGroupsMock[idx] = { ...boardGroupsMock[idx], ...data };
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.put<UpdateBoardGroupPayload, void>(
      `${API_V1}/boards/${boardId}/groups/${groupId}`,
      data,
    );
  },

  delete: async (boardId: string, groupId: string) => {
    if (USE_MOCK) {
      const idx = boardGroupsMock.findIndex((g) => g.id === groupId && g.boardId === boardId);
      if (idx !== -1) boardGroupsMock.splice(idx, 1);
      return Promise.resolve({ data: undefined as void });
    }
    return httpClient.delete<void>(`${API_V1}/boards/${boardId}/groups/${groupId}`);
  },
};
